import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Upload from "@/Components/Icons/Upload";
import { Tooltip } from "@/Components/v2/atoms/ToolTip/Tooltip";
import { VisuallyHiddenInput } from "@/Components/v2/atoms/VisuallyHiddenInput";
import { DeleteConfirmation } from "@/Components/v2/molecule/ConfirmationDialog";

import { Document } from "../../api/schema/openapi";
import { useChat } from "../../context/ChatContext";
import { useArchiveDocument } from "../../hooks/mutations/useArchiveDocument";
import { useUploadDocument } from "../../hooks/mutations/useUploadDocument";
import { useListDocuments } from "../../hooks/queries/useListDocuments";
import { DocumentsSelect } from "./DocumentsSelect";

type DocumentUploadProps = {
  hasPendingAction: boolean;
  disabled?: boolean;
};

type DeleteConfirmation =
  | {
      isOpen: false;
    }
  | {
      isOpen: true;
      document: Document;
    };

export const DocumentUpload = ({ hasPendingAction, disabled = false }: DocumentUploadProps) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const uploadDocument = useUploadDocument();
  const archiveDocument = useArchiveDocument();
  const referenceDocuments = useListDocuments();
  const { enabledDocumentIds, setEnabledDocumentIds } = useChat();
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>({
    isOpen: false,
  });

  const handleUploadDocument = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 5MB");
      event.target.value = "";
      return;
    }

    await uploadDocument.mutateAsync({
      file: file,
      name: file.name,
    });

    toast.success(`${file.name || "File"} uploaded`);
    event.target.value = "";

    await referenceDocuments.refetch();
    // Since the API doesn't return the document ID, we'll let the user select from the refreshed list
  };

  const handleFilterChange = (ids: string[]) => {
    setEnabledDocumentIds(ids);
  };

  const isDisabled = uploadDocument.isPending || showSuccess || disabled;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!uploadDocument.isPending && showSuccess) {
      timeoutId = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [uploadDocument.isPending, showSuccess]);

  useEffect(() => {
    if (uploadDocument.isSuccess) {
      setShowSuccess(true);
    }
  }, [uploadDocument.isSuccess]);

  const handleDocumentArchived = (documentId: string) => {
    const document = referenceDocuments.data?.find((doc) => doc.id === documentId);
    if (!document) return;

    setDeleteConfirmation({
      isOpen: true,
      document,
    });
  };

  const handleDeleteDocument = async () => {
    if (!deleteConfirmation.isOpen) return;

    try {
      await archiveDocument.mutateAsync(deleteConfirmation.document.id);
      setEnabledDocumentIds((prev) => prev.filter((id) => id !== deleteConfirmation.document.id));
      toast.success("Document archived");
      setDeleteConfirmation({ isOpen: false });
    } catch (error) {
      console.error("Archive failed", error);
      toast.error("Failed to archive document");
    }
  };

  return (
    <>
      <Tooltip
        title="Upload a reference document. Documents must be less than 50 pages and smaller than 5MB."
        placement="top"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 280,
              fontSize: 12,
              fontWeight: 500,
            },
          },
        }}
      >
        <span>
          <IconButton component="label" disabled={isDisabled} size="small" sx={{ mr: 1 }}>
            <VisuallyHiddenInput
              type="file"
              accept=".pdf"
              onChange={handleUploadDocument}
              disabled={isDisabled}
            />
            {uploadDocument.isPending ? (
              <CircularProgress size={24} />
            ) : showSuccess ? (
              <CheckCircleIcon color="success" />
            ) : (
              <Upload />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Box>
        <DocumentsSelect
          options={
            referenceDocuments.data?.map((doc) => ({
              label: doc.name,
              value: doc.id,
            })) ?? []
          }
          multiple
          value={enabledDocumentIds}
          onChange={handleFilterChange}
          label=""
          id="documents"
          disabled={hasPendingAction || disabled}
          placeholderOverride="Select Strategy Document(s)"
          width={300}
          onDocumentArchived={handleDocumentArchived}
        />
      </Box>

      {deleteConfirmation.isOpen && (
        <DeleteConfirmation
          open={deleteConfirmation.isOpen}
          title={`Delete ${deleteConfirmation.document.name}?`}
          subtitle={`${deleteConfirmation.document.name} will be permanently removed. This action cannot be undone, and the document will no longer be accessible to anyone in your organization.`}
          onDelete={handleDeleteDocument}
          onCancel={() => setDeleteConfirmation({ isOpen: false })}
        />
      )}
    </>
  );
};
